import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import GuideInfo from '@/components/app/guide/GuideInfo';
import ShareHandoff from '@/components/app/shareFreeContent/ShareHandoff';
import SharePayment from '@/components/app/shareFreeContent/SharePayment';
import SharePrimer from '@/components/app/shareFreeContent/SharePrimer';
import { useShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import Loader from '@/components/Loader';
import StripeElementsProvider from '@/components/purchase/StripeElementsProvider';
import UpsellBanner from '@/components/UpsellBanner';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useUserState } from '@/hooks/store';
import { userCanTrial } from '@/utils/subscriptions';
import chevronLeft from 'icons/chevron-left.svg';

import messages from './messages';
import { BackButton, Grid, Wrapper } from './styles';
import { GuideLayoutProps } from './types';

const GuideLayout = ({ guide, program, rotateBackButton }: GuideLayoutProps) => {
	const router = useRouter();
	const user = useUserState();
	const { logEvent } = useAnalytics();
	const { onStop, hideControls, shouldPreventAppNav, playerMode } = useMediaPlayerContext();
	const { shareToken, shareSenderFirstName, isFetchingShare } = useShareContext();
	const { formatMessage } = useIntl();
	const { setIsModalOpen } = useSimplifiedSignupModalContext();

	const [isVisible, setIsVisible] = useState(true);
	const [startedContent, setStartedContent] = useState(false);

	const isPreviewMode = !guide?.is_free && !user?.subscription?.valid;

	const shouldHideBackButton = shouldPreventAppNav && !program;

	const onBackClick = async () => {
		logEvent({ eventName: 'Audio Session : Back : Clicked' });
		// Deliberately shows no transient loader: `next/navigation` exposes no
		// navigation-complete signal, and `router.back()` is a silent no-op when there is
		// no history entry to pop (a deep link straight to this route), which would strand
		// a spinner over the guide content permanently.
		router.back();
	};

	useEffect(() => {
		logEvent({ eventName: 'Player Program : Landed' });
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		return setIsVisible(false);
	}, [shareSenderFirstName, shareToken, program, guide, logEvent]);

	useEffect(() => {
		logEvent({
			eventName: 'Screen : Viewed',
			eventProps: {
				screen: 'Signup Primer Screen',
				source: 'Shared Link',
				user_eligible_for_30D_trial: userCanTrial(user),
				share_token: shareToken as string,
				sender_first_name: shareSenderFirstName ? (shareSenderFirstName as string) : null,
				author_name: program?.author?.name || null,
				program_title: program?.title || null,
				program_id: program?.id || null,
				meditation_type: program?.meditation_type || null,
				narrator_name: program?.narrator?.name || null,
				program_language: program?.language || null,
				program_is_free: guide?.is_free || false,
				narrator_id: program?.narrator?.id || null,
			},
		});
	}, [shareSenderFirstName, shareToken, program, guide, logEvent, user]);

	useEffect(() => {
		if (playerMode === 'stopped' && isPreviewMode && startedContent) {
			setIsModalOpen(true);
		}
		if (playerMode === 'playing' && !startedContent) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setStartedContent(true);
		}
	}, [onStop, isPreviewMode, playerMode, setIsModalOpen, startedContent]);

	const showBackButton = !shouldHideBackButton && !hideControls && !shareToken && !isPreviewMode;

	return (
		<StripeElementsProvider isStripeNext>
			<UpsellBanner />
			<Grid columns={4}>
				<Wrapper isVisible={isVisible}>
					<SharePrimer isPreviewMode={isPreviewMode} />

					<SharePayment />

					<ShareHandoff />

					{showBackButton && (
						<BackButton
							aria-label={formatMessage(messages.backButtonLabel)}
							color="white"
							onPress={onBackClick}
							$rotate={rotateBackButton}
							Icon={chevronLeft}
							data-testid="back-button"
						/>
					)}
					{isFetchingShare ? <Loader /> : <GuideInfo program={program} guide={guide} />}
				</Wrapper>
			</Grid>
		</StripeElementsProvider>
	);
};

export default GuideLayout;
